// @ts-expect-error js lib
import * as KDTreeLib from 'static-kdtree/kdtree';
import { MathUtils, Vector3 } from 'three';

import { PrefabModelType } from 'api/models';
import { calculateFullQuaternion } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    getAnchorPart,
    dragColliderCache,
    instanceToPoiIndexMap
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { EPSILON } from 'shared/constants';

import type { ColliderPoint } from 'app/modules/wallforming/components/scenes/objects/helpers/types/colliderPoint';
import type { WallEdge } from 'app/modules/wallforming/components/scenes/objects/helpers/types/wallEdge';
import type {
    ColliderPointWithType,
    ColliderSliceState
} from 'app/modules/wallforming/components/state-management/placement/states/colliderSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

const tmpWorld = new Vector3();
const colliderCache: Record<string, Vector3[]> = {};

const edgeDirection = new Vector3();
const edgeToPoint = new Vector3();
const edgeProjected = new Vector3();

let rebuildTreeTimer: ReturnType<typeof requestAnimationFrame> | undefined;

function scheduleRebuildTree(get: () => PlacementState): void {
    if (rebuildTreeTimer !== undefined) {
        return;
    }

    rebuildTreeTimer = requestAnimationFrame(() => {
        rebuildTreeTimer = undefined;
        get().rebuildTree();
    });
}

function flattenWithType(colliders: Record<string, ColliderPoint[]>): ColliderPointWithType[] {
    return Object.entries(colliders).flatMap(([ownerType, arr]) => arr.map((c) => ({ ...c, ownerType })));
}

function areCollidersEqual(arr1: ColliderPoint[], arr2: ColliderPoint[]): boolean {
    if (arr1.length !== arr2.length) {
        return false;
    }

    return arr1.every((c1, i) => {
        const c2 = arr2[i];
        return (
            c1.id === c2.id &&
            c1.relationId === c2.relationId &&
            c1.poiIndex === c2.poiIndex &&
            c1.poiGuids.length === c2.poiGuids.length &&
            c1.poiGuids.every((poiGuid, index) => poiGuid === c2.poiGuids[index]) &&
            Math.abs(c1.position.x - c2.position.x) < EPSILON &&
            Math.abs(c1.position.y - c2.position.y) < EPSILON &&
            Math.abs(c1.position.z - c2.position.z) < EPSILON
        );
    });
}

/** Builds a flat wall edge list for the given set of POI Guids */
function buildWallEdges(edgesByPoi: Map<string, WallEdge[]>, poiGuids: ReadonlySet<string>): WallEdge[] {
    const result: WallEdge[] = [];
    for (const guid of poiGuids) {
        const edges = edgesByPoi.get(guid);
        if (edges) {
            for (const e of edges) {
                result.push(e);
            }
        }
    }
    return result;
}

export const createColliderSlice: StateCreator<PlacementState, [], [], ColliderSliceState> = (set, get) => ({
    colliders: {},
    tree: null,
    flatColliders: [],
    buildingWallEdges: [],
    buildingWallEdgesByPoi: new Map(),

    setColliders: (points, type) => {
        set((state) => ({
            colliders: { ...state.colliders, [type]: points }
        }));
        scheduleRebuildTree(get);
    },

    addColliders: (points, type) => {
        set((state) => ({
            colliders: {
                ...state.colliders,
                [type]: [...(state.colliders[type] || []), ...points]
            }
        }));
        scheduleRebuildTree(get);
    },

    removeCollidersByRelation: (relationId) => {
        set((state) => {
            const updated: Record<string, ColliderPoint[]> = {};
            for (const [type, arr] of Object.entries(state.colliders)) {
                updated[type] = arr.filter((c) => c.relationId !== relationId);
            }
            return { colliders: updated };
        });
        scheduleRebuildTree(get);
    },

    removeCollidersByRelations: (relationIds) => {
        if (!relationIds || relationIds.length === 0) {
            return;
        }
        const relationSet = new Set(relationIds);
        set((state) => {
            const updated: Record<string, ColliderPoint[]> = {};
            for (const [type, arr] of Object.entries(state.colliders)) {
                updated[type] = arr.filter((c) => !relationSet.has(c.relationId));
            }
            return { colliders: updated };
        });
        scheduleRebuildTree(get);
    },

    removeCollidersByPOI: (poiIndex) => {
        set((state) => {
            const updated: Record<string, ColliderPoint[]> = {};
            for (const [type, arr] of Object.entries(state.colliders)) {
                updated[type] = arr.filter((c) => c.poiIndex !== poiIndex);
            }
            return { colliders: updated };
        });
        scheduleRebuildTree(get);
    },

    removeCollidersByOwner: (ownerType) => {
        set((state) => {
            const updated = { ...state.colliders };
            delete updated[ownerType];
            return { colliders: updated };
        });
        scheduleRebuildTree(get);
    },

    extractLocalColliders: (prefabName, prefab) => {
        if (!prefabName) {
            return [];
        }

        if (!colliderCache[prefabName]) {
            if (!prefab || prefab.type === PrefabModelType.Group) {
                return [];
            }

            const localPoints: Vector3[] = [];

            if (
                prefab.elementType === 0 ||
                prefab.elementType === 1 ||
                prefab.elementType === 2 ||
                prefab.elementType === 3 ||
                prefab.elementType === 4 ||
                prefab.elementType === 5 ||
                prefab.elementType === 6 ||
                prefab.elementType === 88
            ) {
                for (const layer of prefab.logicLayers) {
                    if (layer.layerType !== 'COVERAREA') {
                        continue;
                    }

                    for (const geometry of layer.geometries) {
                        if (geometry.geometryType !== 'Polyline') {
                            continue;
                        }

                        for (const pointModel of geometry.points) {
                            const point = new Vector3(pointModel.x, pointModel.y, pointModel.z);
                            if (!localPoints.find((p) => p.equals(point))) {
                                localPoints.push(point);
                            }
                        }
                    }
                }
            }

            if (prefab.elementType === 53) {
                localPoints.push(new Vector3(0, 0, 0));
            }

            colliderCache[prefabName] = localPoints;
        }

        return colliderCache[prefabName];
    },

    computeInstanceColliders: (articleInstanceId, articleInstance, articleRepresentations, poiIndex, poiGuids) => {
        if (!articleInstance) {
            return [];
        }

        const colliders: ColliderPoint[] = [];
        const relatedPoiGuids = poiGuids?.length
            ? poiGuids
            : [get().pois[poiIndex]?.poi.poiGuid].filter((poiGuid): poiGuid is string => Boolean(poiGuid));

        for (const representation of articleRepresentations) {
            const prefabs =
                representation.children && representation.children.length > 0
                    ? representation.children.filter((c) => c.type === PrefabModelType.SubPart)
                    : [representation];

            for (const prefab of prefabs) {
                const part = articleInstance.parts[prefab.name];
                if (!part) {
                    continue;
                }

                const localPoints = get().extractLocalColliders(prefab.name, prefab);
                const quaternion = calculateFullQuaternion(part.alignment, part.rotation);
                const scale = part.scale || { x: 1, y: 1, z: 1 };

                localPoints.forEach((local, i) => {
                    // Apply scale before rotation and translation
                    const scaled = tmpWorld.copy(local).multiply(scale);
                    const worldPos = scaled.applyQuaternion(quaternion).add(part.position);
                    colliders.push({
                        id: `${articleInstanceId}_${prefab.name}_${i}`,
                        poiIndex,
                        poiGuids: relatedPoiGuids,
                        relationId: articleInstanceId,
                        position: worldPos.clone(),
                        name: prefab.name,
                        alignment: part.alignment.clone().normalize()
                    });
                });
            }
        }

        return colliders;
    },

    computeArticleInsertionPoint: (articleId, articleInstance) => {
        if (!articleId || !articleInstance) {
            return null;
        }

        const state = get();
        const partsCount = Object.keys(articleInstance.parts).length;
        const origin = new Vector3(0, 0, 0);

        if (state.insertionPoint.equals(origin)) {
            if (partsCount === 0) {
                return null;
            }

            const part = getAnchorPart(articleInstance);
            if (part) {
                const quaternion = calculateFullQuaternion(part.alignment, part.rotation);
                tmpWorld.copy(origin).applyQuaternion(quaternion).add(part.position);
                return tmpWorld.clone();
            }
        } else {
            for (const [name, part] of Object.entries(articleInstance.parts)) {
                const localPoints = get().extractLocalColliders(name);

                const referencePoint = localPoints.find((local) => local.equals(state.insertionPoint));
                if (referencePoint) {
                    const quaternion = calculateFullQuaternion(part.alignment, part.rotation);
                    tmpWorld.copy(referencePoint).applyQuaternion(quaternion).add(part.position);
                    return tmpWorld.clone();
                }
            }
        }

        return null;
    },

    batchSetArticleColliders: (newColliders, relationIds) => {
        set((state) => {
            const type = 'article';
            const existing = state.colliders[type] || [];

            const filtered = existing.filter((c) => !relationIds.has(c.relationId));
            const updatedColliders = [...filtered, ...newColliders];

            const seenIds = new Set<string>();
            const uniqueColliders = updatedColliders.filter((c) => {
                if (seenIds.has(c.id)) {
                    return false;
                }
                seenIds.add(c.id);
                return true;
            });

            if (areCollidersEqual(existing, uniqueColliders)) {
                return state;
            }

            return {
                colliders: {
                    ...state.colliders,
                    [type]: uniqueColliders
                }
            };
        });

        scheduleRebuildTree(get);
    },

    updateInstanceColliders: (articleInstanceId, articleInstance, articleRepresentations, poiIndex) => {
        set((state) => {
            const newColliders = state.computeInstanceColliders(
                articleInstanceId,
                articleInstance,
                articleRepresentations,
                poiIndex
            );

            if (!newColliders || newColliders.length === 0) {
                return state;
            }

            const type = 'article';
            const existing = state.colliders[type] || [];

            const filtered = existing.filter((c) => c.relationId !== articleInstanceId);
            const updatedColliders = [...filtered, ...newColliders];

            const seenIds = new Set<string>();
            const uniqueColliders = updatedColliders.filter((c) => {
                if (seenIds.has(c.id)) {
                    return false;
                }
                seenIds.add(c.id);
                return true;
            });

            if (areCollidersEqual(existing, uniqueColliders)) {
                return state;
            }

            return {
                colliders: {
                    ...state.colliders,
                    [type]: uniqueColliders
                }
            };
        });

        scheduleRebuildTree(get);
    },

    updateBuildingColliders: (newColliders) => {
        set((state) => {
            if (!newColliders || newColliders.length === 0) {
                return state;
            }

            const type = 'building';
            const existing = state.colliders[type] || [];
            const filtered = existing.filter((c) => c.poiIndex !== state.poiSelection.current.index);

            const seenIds = new Set<string>();
            const uniqueNewColliders = newColliders.filter((c) => {
                if (seenIds.has(c.id)) {
                    return false;
                }
                seenIds.add(c.id);
                return true;
            });
            const updatedColliders = [...filtered, ...uniqueNewColliders];

            if (areCollidersEqual(existing, updatedColliders)) {
                return state;
            }

            return {
                colliders: {
                    ...state.colliders,
                    [type]: updatedColliders
                }
            };
        });

        scheduleRebuildTree(get);
    },

    setBuildingWallEdges: (edges) => {
        const edgesByPoi = new Map<string, WallEdge[]>();
        for (const edge of edges) {
            const group = edgesByPoi.get(edge.poiGuid);
            if (group) {
                group.push(edge);
            } else {
                edgesByPoi.set(edge.poiGuid, [edge]);
            }
        }
        set({ buildingWallEdges: edges, buildingWallEdgesByPoi: edgesByPoi });
    },

    findNearestPointOnBuildingEdges: (position, threshold, poiGuids?) => {
        const { buildingWallEdges, buildingWallEdgesByPoi } = get();
        if (buildingWallEdges.length === 0) {
            return null;
        }

        let minDistance = threshold;
        let bestPoint: Vector3 | null = null;

        const edges: WallEdge[] =
            poiGuids && poiGuids.size > 0 ? buildWallEdges(buildingWallEdgesByPoi, poiGuids) : buildingWallEdges;

        for (const { start, end } of edges) {
            edgeDirection.subVectors(end, start);
            const lenSq = edgeDirection.lengthSq();
            if (lenSq < 1e-12) {
                continue;
            }
            edgeToPoint.subVectors(position, start);
            let t = edgeToPoint.dot(edgeDirection) / lenSq;
            t = Math.max(0, Math.min(1, t));
            edgeProjected.copy(start).addScaledVector(edgeDirection, t);

            const distance = position.distanceTo(edgeProjected);
            if (distance < minDistance) {
                minDistance = distance;
                bestPoint = edgeProjected.clone();
            }
        }

        return bestPoint;
    },

    findNearestBuildingEdgeSegment: (position, threshold, poiGuids?) => {
        const { buildingWallEdges, buildingWallEdgesByPoi } = get();
        if (buildingWallEdges.length === 0) {
            return null;
        }

        let minDistance = threshold;
        let bestEdge: WallEdge | null = null;

        const edges: WallEdge[] =
            poiGuids && poiGuids.size > 0 ? buildWallEdges(buildingWallEdgesByPoi, poiGuids) : buildingWallEdges;

        for (const edge of edges) {
            const { start, end } = edge;
            edgeDirection.subVectors(end, start);
            const lenSq = edgeDirection.lengthSq();
            if (lenSq < 1e-12) {
                continue;
            }
            edgeToPoint.subVectors(position, start);
            let t = edgeToPoint.dot(edgeDirection) / lenSq;
            t = Math.max(0, Math.min(1, t));
            edgeProjected.copy(start).addScaledVector(edgeDirection, t);
            const distance = position.distanceTo(edgeProjected);
            if (distance < minDistance) {
                minDistance = distance;
                bestEdge = edge;
            }
        }

        return bestEdge;
    },

    calculateSnappingPosition: (draggedInstanceId, edgeSnapEnabled = true, poiGuids?, threshold = 0.1) => {
        const state = get();

        const selectedDragRefs = state.getSelectedArticleDragReference(draggedInstanceId);
        if (!selectedDragRefs?.articleRef.current) {
            return null;
        }

        const articleInstance = selectedDragRefs.articleRef.current.articleInstances[draggedInstanceId];
        if (!articleInstance) {
            return null;
        }

        // Resolve the instance's POI for collider computation
        const instancePoiIndex = instanceToPoiIndexMap.get(draggedInstanceId) ?? state.poiSelection.current.index ?? 0;

        // Use cached collider positions when available — avoids recomputing on every snap frame
        let cachedPositions = dragColliderCache.get(draggedInstanceId);
        if (!cachedPositions) {
            const colliders = state.computeInstanceColliders(
                draggedInstanceId,
                articleInstance,
                selectedDragRefs.articleRef.current.articleRepresentations,
                instancePoiIndex
            );
            cachedPositions = colliders.map((c) => c.position.clone());
            dragColliderCache.set(draggedInstanceId, cachedPositions);
        }

        // Adjust snapping threshold according to camera zoom (higher zoom => smaller threshold)
        let adjustedThreshold = threshold;
        const controls = state.mainCameraControlsRef.current;
        if (controls) {
            const zoom = controls.camera.zoom;
            // Convert zoom into a stable 0–1 scale using log compression
            const normalized = Math.log10(zoom) / Math.log10(9000);
            const inverted = 1 - MathUtils.clamp(normalized, 0, 1);
            const factor = MathUtils.lerp(0.1, 1, inverted);
            adjustedThreshold = threshold * factor;
        }

        let snappingPosition: Vector3[] | null = null;
        let minDistance = adjustedThreshold;

        for (const position of cachedPositions) {
            // Allow snapping to colliders on any POI
            const result = state.findNearest(position, {
                excludeRelationId: draggedInstanceId,
                maxDistance: adjustedThreshold,
                maxResultsCount: 1,
                neighborExpansionFactor: 5
            });

            if (result) {
                const newDistance = result.position.distanceTo(position);
                if (newDistance < minDistance) {
                    minDistance = newDistance;
                    snappingPosition = [result.position.clone(), position.clone()];
                }
            }
        }

        // Only check building wall edge interiors when no corner snap was found (and caller allows it)
        // Scope to the instance's POI guid for performance (falls back to all edges if not available)
        if (edgeSnapEnabled && !snappingPosition) {
            // Derive the POI guid for this instance if not provided by caller
            const resolvedPoiGuids: ReadonlySet<string> | undefined =
                poiGuids ??
                (() => {
                    const poiGuid = state.pois[instancePoiIndex]?.poi?.poiGuid;
                    return poiGuid ? new Set([poiGuid]) : undefined;
                })();

            for (const position of cachedPositions) {
                const edgePoint = state.findNearestPointOnBuildingEdges(position, threshold, resolvedPoiGuids);
                if (edgePoint) {
                    const newDistance = edgePoint.distanceTo(position);
                    if (newDistance < minDistance) {
                        minDistance = newDistance;
                        snappingPosition = [edgePoint, position.clone()];
                    }
                }
            }
        }

        return snappingPosition;
    },

    findNearest: (position, options = {}) => {
        const { tree, flatColliders } = get();
        if (!tree || flatColliders.length === 0) {
            return null;
        }

        const {
            maxDistance = Infinity,
            excludeRelationId,
            includePoi,
            maxResultsCount = 1,
            neighborExpansionFactor = 5
        } = options;

        const neighborsCount = Math.min(flatColliders.length, maxResultsCount * neighborExpansionFactor);
        const indices = tree.knn([position.x, position.y, position.z], neighborsCount);

        for (const idx of indices) {
            const c = flatColliders[idx];
            if (!c) {
                continue;
            }
            if (excludeRelationId && c.relationId === excludeRelationId) {
                continue;
            }
            if (includePoi !== undefined && c.poiIndex !== includePoi) {
                continue;
            }

            const dist = c.position.distanceTo(position);
            if (dist <= maxDistance) {
                return c;
            }
        }

        return null;
    },

    rebuildTree: () => {
        const colliders = get().colliders;
        const flat = flattenWithType(colliders);
        if (flat.length === 0) {
            set({ tree: null, flatColliders: [] });
            return;
        }

        const pts = flat.map((c) => [c.position.x, c.position.y, c.position.z]);
        const createKDTree = (KDTreeLib as KDTreeLib).default || KDTreeLib;
        const tree = createKDTree(pts);
        set({ tree, flatColliders: flat });
    }
});
